import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useClientWaivers, SignedWaiver } from "@/hooks/use-client-waivers";
import { WAIVER_TYPE_LABELS } from "@/hooks/use-waiver-templates";
import { format, formatDistanceToNow, isPast, isFuture, addDays } from "date-fns";
import {
  FileText,
  CheckCircle2,
  AlertTriangle,
  Clock,
  XCircle,
  Eye,
  Loader2,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface ClientWaiversCardProps {
  clientId: string;
}

export function ClientWaiversCard({ clientId }: ClientWaiversCardProps) {
  const { signedWaivers, loading, error, revokeWaiver, checkWaiverStatus } =
    useClientWaivers(clientId);
  const [viewingWaiver, setViewingWaiver] = useState<SignedWaiver | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);

  const status = checkWaiverStatus();

  const getWaiverStatus = (waiver: SignedWaiver) => {
    if (!waiver.is_valid) {
      return { label: "Revoked", color: "destructive", icon: XCircle };
    }
    if (waiver.expires_at) {
      if (isPast(new Date(waiver.expires_at))) {
        return { label: "Expired", color: "destructive", icon: AlertTriangle };
      }
      const thirtyDaysFromNow = addDays(new Date(), 30);
      if (new Date(waiver.expires_at) <= thirtyDaysFromNow) {
        return { label: "Expiring Soon", color: "warning", icon: Clock };
      }
    }
    return { label: "Valid", color: "success", icon: CheckCircle2 };
  };

  const handleRevoke = async (waiverId: string) => {
    setRevoking(waiverId);
    await revokeWaiver(waiverId);
    setRevoking(null);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          {error}
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Signed Waivers
              </CardTitle>
              <CardDescription>
                {signedWaivers.length} waiver{signedWaivers.length !== 1 ? "s" : ""} on file
              </CardDescription>
            </div>
            {!status.hasAllRequired && (
              <Badge variant="destructive" className="gap-1">
                <AlertTriangle className="h-3 w-3" />
                {status.missingRequired.length} Required Missing
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {signedWaivers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-10 w-10 mx-auto mb-2 opacity-50" />
              <p>No waivers signed yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {signedWaivers.map((waiver) => {
                const waiverStatus = getWaiverStatus(waiver);
                const StatusIcon = waiverStatus.icon;

                return (
                  <div
                    key={waiver.id}
                    className="flex items-center justify-between p-3 rounded-lg border bg-card"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div
                        className={cn(
                          "p-2 rounded-full",
                          waiverStatus.color === "success" && "bg-success/15",
                          waiverStatus.color === "warning" && "bg-warning/15",
                          waiverStatus.color === "destructive" && "bg-destructive/15"
                        )}
                      >
                        <StatusIcon
                          className={cn(
                            "h-4 w-4",
                            waiverStatus.color === "success" && "text-success",
                            waiverStatus.color === "warning" && "text-warning",
                            waiverStatus.color === "destructive" && "text-destructive"
                          )}
                        />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium truncate">
                          {waiver.template?.name || "Unknown Waiver"}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span>
                            Signed {format(new Date(waiver.signed_at), "MMM d, yyyy")}
                          </span>
                          {waiver.expires_at && (
                            <>
                              <span>•</span>
                              <span>
                                {isPast(new Date(waiver.expires_at))
                                  ? `Expired ${formatDistanceToNow(new Date(waiver.expires_at), { addSuffix: true })}`
                                  : `Expires ${formatDistanceToNow(new Date(waiver.expires_at), { addSuffix: true })}`}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge
                        variant={
                          waiverStatus.color === "success"
                            ? "default"
                            : waiverStatus.color === "warning"
                            ? "secondary"
                            : "destructive"
                        }
                        className={cn(
                          waiverStatus.color === "success" &&
                            "bg-success/15 text-success hover:bg-success/15"
                        )}
                      >
                        {waiverStatus.label}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setViewingWaiver(waiver)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Waiver Dialog */}
      <AlertDialog
        open={!!viewingWaiver}
        onOpenChange={() => setViewingWaiver(null)}
      >
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {viewingWaiver?.template?.name || "Waiver Details"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Signed on{" "}
              {viewingWaiver?.signed_at &&
                format(new Date(viewingWaiver.signed_at), "MMMM d, yyyy 'at' h:mm a")}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {viewingWaiver && (
            <div className="space-y-4">
              <div className="p-4 bg-muted/10 rounded-lg">
                <p className="text-xs text-muted-foreground mb-2">Signature</p>
                {viewingWaiver.signature_data && (
                  <img
                    src={viewingWaiver.signature_data}
                    alt="Signature"
                    className="max-h-24 bg-card rounded border"
                  />
                )}
              </div>

              {viewingWaiver.initials_data && (
                <div className="p-4 bg-muted/10 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-2">Initials</p>
                  <img
                    src={viewingWaiver.initials_data}
                    alt="Initials"
                    className="max-h-16 bg-card rounded border"
                  />
                </div>
              )}

              <div className="text-xs text-muted-foreground space-y-1">
                <p>Waiver ID: {viewingWaiver.id}</p>
                {viewingWaiver.user_agent && (
                  <p className="truncate">Device: {viewingWaiver.user_agent}</p>
                )}
              </div>
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel>Close</AlertDialogCancel>
            {viewingWaiver?.is_valid && (
              <AlertDialogAction
                onClick={() => {
                  if (viewingWaiver) {
                    handleRevoke(viewingWaiver.id);
                    setViewingWaiver(null);
                  }
                }}
                className="bg-destructive hover:bg-destructive/90"
              >
                Revoke Waiver
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
